import { Directive, OnInit, OnDestroy, AfterViewInit } from '@angular/core';
import { Dropdown } from 'primeng/dropdown';
import { Subject, takeUntil } from 'rxjs';

/**
 * Directive to reset scroll position when filtering PrimeNG dropdowns with virtual scrolling.
 * Fixes the issue where filtered items appear outside the viewport due to transform not being reset.
 * 
 * Usage:
 * <p-dropdown dropdownScrollReset [virtualScroll]="true" [filter]="true">
 * </p-dropdown>
 */
@Directive({
  selector: 'p-dropdown[dropdownScrollReset]',
  host: {
    '[class.dropdown-scroll-reset]': 'true'
  }
})
export class DropdownScrollResetDirective implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private dropdown: Dropdown;

  constructor(dropdown: Dropdown) {
    this.dropdown = dropdown;
  }

  ngOnInit(): void {
    if (!this.dropdown.virtualScroll || !this.dropdown.filter) {
      console.warn('DropdownScrollResetDirective should only be used with virtualScroll and filter enabled');
    }
  }

  ngAfterViewInit(): void {
    this.setupScrollReset();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupScrollReset(): void {
    if (!this.dropdown) {
      return;
    }

    this.dropdown.onFilter
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.onFilterChange(event);
      });
  }

  private onFilterChange(event: any): void {
    requestAnimationFrame(() => {
      this.resetScroll();
    });

    // Additional attempt after a small delay to ensure filter has been applied
    setTimeout(() => {
      this.resetScroll();
    }, 50);
  }

  private resetScroll(): void {
    const ul = document.querySelector(
      '.p-dropdown-items.p-scroller-content, .p-dropdown-items, ul.p-scroller-content'
    ) as HTMLElement;
    
    if (ul && ul.style.transform && ul.style.transform !== 'translate3d(0px, 0px, 0px)') {
      ul.style.transform = 'translate3d(0px, 0px, 0px)';
    }
    
    const scrollContainers = [
      '.p-dropdown-panel .p-scroller',
      '.p-dropdown-panel cdk-virtual-scroll-viewport',
      '.p-scroller',
      '.cdk-virtual-scroll-viewport'
    ];
    
    for (const selector of scrollContainers) {
      const container = document.querySelector(selector) as HTMLElement;
      if (container && container.scrollTop > 0) {
        container.scrollTop = 0;
      }
    }

    const contentWrapper = document.querySelector('.cdk-virtual-scroll-content-wrapper') as HTMLElement;
    if (contentWrapper && contentWrapper.style.transform) {
      contentWrapper.style.transform = 'translateY(0px)';
    }
  }
}